import type { Session } from '@supabase/supabase-js';
import { useCallback, useEffect, useState } from 'react';
import { ensureSession, onSessionChange } from './session';

export type SessionStatus = 'loading' | 'ready' | 'error';

export interface SessionState {
  session: Session | null;
  status: SessionStatus;
  /** Why the bootstrap failed, for the retry screen. */
  error: string | null;
  retry: () => void;
}

/**
 * The anonymous session the whole app runs on.
 *
 * There is no signed-out state to model any more — either there is a session or
 * the device could not reach Supabase, and the second one is a transient error
 * with a retry, not a screen asking anybody to log in.
 */
export function useSession(): SessionState {
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;

    setStatus('loading');
    setError(null);

    ensureSession().then(
      (established) => {
        if (cancelled) return;
        setSession(established);
        setStatus('ready');
      },
      (cause: unknown) => {
        if (cancelled) return;
        setError(cause instanceof Error ? cause.message : String(cause));
        setStatus('error');
      },
    );

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  // Keeps up with a token refresh or a `resetIdentity`, both of which replace
  // the session without going through the bootstrap above.
  useEffect(() => {
    return onSessionChange((next) => {
      if (next) setSession(next);
    });
  }, []);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { session, status, error, retry };
}
